import { useEffect, useLayoutEffect, useState, useSyncExternalStore } from 'react';

/**
 * D798: the console's theme choice — "Igual ao sistema" (default), "Claro" or "Escuro" — the same three options as the native
 * apps (D765). It is a per-device convenience, kept in this browser (like the apps keep it on the phone): nothing on the
 * server, nothing shared. Storage can be missing or blocked (a private window, cleared site data): every read and write is
 * guarded and the default is the system's.
 *
 * The theme belongs to the CONSOLE, never to a sign-in (owner, 2026-10-28: "tela de login não pode ser mexida"): only the
 * console shell (`ConsoleShell`, through {@link useConsoleTheme}) puts `data-theme="dark"` on `<html>`, and it takes it off
 * when it leaves. Every screen before the shell — sign-in, recovery, the MFA step, the boot splash — has no attribute and is
 * always the light design. The stylesheet of `velloThemeCss(light, dark)` reads only that attribute.
 */
export type ThemePreference = 'system' | 'light' | 'dark';
export type ColorScheme = 'light' | 'dark';

export const THEME_PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'];

export const THEME_STORAGE_KEY = 'vello.theme';
const CHANGE_EVENT = 'vello-theme-change';
const DARK_QUERY = '(prefers-color-scheme: dark)';

let darkInstalled = false;

/** Called by `installVelloTheme(…, { dark: true })`: this page has a dark surface the console shell may switch on. */
export function enableDarkTheme(): void {
  darkInstalled = true;
}

function isPreference(v: unknown): v is ThemePreference {
  return v === 'system' || v === 'light' || v === 'dark';
}

/** The stored choice, or `system`. */
export function readThemePreference(): ThemePreference {
  try {
    const v = globalThis.localStorage?.getItem(THEME_STORAGE_KEY);
    return isPreference(v) ? v : 'system';
  } catch {
    return 'system';
  }
}

/** Store and announce a choice (the console shell, when on screen, applies it). */
export function setThemePreference(p: ThemePreference): void {
  try {
    if (p === 'system') globalThis.localStorage?.removeItem(THEME_STORAGE_KEY);
    else globalThis.localStorage?.setItem(THEME_STORAGE_KEY, p);
  } catch {
    // Storage blocked: the choice still holds for this page.
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

function systemScheme(): ColorScheme {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

/** The scheme a choice resolves to now. */
export function resolveColorScheme(p: ThemePreference): ColorScheme {
  return p === 'system' ? systemScheme() : p;
}

/** The current choice and its setter; follows changes made elsewhere on the page. */
export function useThemePreference(): readonly [ThemePreference, (p: ThemePreference) => void] {
  const [pref, setPref] = useState<ThemePreference>(readThemePreference);
  useEffect(() => {
    const sync = (): void => setPref(readThemePreference());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  return [pref, setThemePreference];
}

/**
 * The console shell's side: while it is on screen, `<html data-theme="dark">` follows the choice (and the system's scheme
 * when the choice is `system`); when it leaves (sign-out), the attribute goes and the page is light again. A no-op on a page
 * installed without the dark surface.
 */
export function useConsoleTheme(): void {
  useLayoutEffect(() => {
    if (!darkInstalled || typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    const apply = (): void => {
      if (resolveColorScheme(readThemePreference()) === 'dark') root.dataset.theme = 'dark';
      else delete root.dataset.theme;
    };
    apply();
    const mq = typeof window.matchMedia === 'function' ? window.matchMedia(DARK_QUERY) : undefined;
    window.addEventListener(CHANGE_EVENT, apply);
    window.addEventListener('storage', apply);
    mq?.addEventListener?.('change', apply);
    return () => {
      window.removeEventListener(CHANGE_EVENT, apply);
      window.removeEventListener('storage', apply);
      mq?.removeEventListener?.('change', apply);
      delete root.dataset.theme;
    };
  }, []);
}

function subscribeScheme(onChange: () => void): () => void {
  if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') return () => undefined;
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  return () => observer.disconnect();
}

function schemeNow(): ColorScheme {
  return typeof document !== 'undefined' && document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

/**
 * The scheme the page is drawn in right now (read from `<html data-theme>`, so always `light` outside the console shell) —
 * for what CSS cannot switch: the basemap style, the sidebar's mark.
 */
export function useColorScheme(): ColorScheme {
  return useSyncExternalStore(subscribeScheme, schemeNow, () => 'light');
}
